import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import {
  Accordion,
  Avatar,
  Badge,
  Button,
  Checkbox,
  SpinnerLoader,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import {
  ChevronDown,
  DoorOpen,
  LayersThree02,
  Users01,
} from '@kula-ai/icons-core';
import { Box, Flex } from '@chakra-ui/react';
import { colors } from '@/theme/foundations/colors';
import { memberCountLabel, onJobTagSummary } from './useRecipientSections';
import {
  costOfAdding,
  EMPTY_COST_MODEL,
  recipientLimitHint,
  RECIPIENT_OVER_LIMIT_HINT,
  tallyRecipients,
  type RecipientCostModel,
} from './recipientLimits';
import type { RecipientOption, RecipientSection } from './useRecipientSections';

interface SectionChrome {
  Icon: ComponentType<{ size?: number; color?: string }>;
}

const SECTION_CHROME: Record<RecipientSection['id'], SectionChrome> = {
  job_roles: { Icon: LayersThree02 },
  kula_roles: { Icon: DoorOpen },
  users: { Icon: Users01 },
};

const SECTION_ORDER: RecipientSection['id'][] = [
  'job_roles',
  'kula_roles',
  'users',
];

/**
 * The panel's own size, in the two pieces react-select has to know about.
 *
 * `LIST_MAX_HEIGHT` is what the scrolling list gets when there is room for
 * everything; `FOOTER_HEIGHT` is the Cancel/Add strip below it, which never
 * scrolls — it is the part that must stay on screen, since a panel whose Add
 * button is past the fold cannot be committed at all.
 */
const LIST_MAX_HEIGHT = 360;
const FOOTER_HEIGHT = 57;

/**
 * Handed to react-select as `maxMenuHeight`, so the placement it picks is chosen
 * against the height this panel actually wants rather than its own 300 default.
 */
export const RECIPIENT_MENU_MAX_HEIGHT = LIST_MAX_HEIGHT + FOOTER_HEIGHT;

/** Breathing room between the panel and the edge of the window. */
const VIEWPORT_GUTTER = 12;

/**
 * Floor for the list once the panel is constrained to the space it has: about
 * three rows. Below this the panel is not worth showing downwards at all.
 */
const LIST_MIN_HEIGHT = 160;

/**
 * Handed to react-select as `minMenuHeight` — the point at which it stops
 * shrinking the panel and flips it above the field instead.
 *
 * It has to be this panel's own floor. Left at react-select's 140, there is a
 * band where it keeps the panel below with less room than the panel will shrink
 * to, and the footer goes back under the fold — the whole bug, in a narrower
 * window.
 */
export const RECIPIENT_MENU_MIN_HEIGHT = LIST_MIN_HEIGHT + FOOTER_HEIGHT;

export interface RecipientPickerConfig {
  sections: RecipientSection[];
  /** Every option the picker has rendered, so a chip keeps its label. */
  optionByValue: Map<string, { value: string; label: string; image?: string }>;
  /** The scroll container has reached the end of the list — page in the rest. */
  onEndReached?: () => void;
  /**
   * What each row weighs against the cap. Absent on a surface that does not
   * enforce one — the interview-plan composer configures a send for later and
   * often has no job, so a count there would be a guess presented as a rule.
   */
  costModel?: RecipientCostModel;
  /** The cap for this field. Absent means no cap. */
  limit?: number;
  /**
   * What the other field sharing this budget already holds. Cc and Bcc are two
   * react-selects spending one 450, and neither can see the other's value — this
   * is how the one that can (the composer) tells them.
   */
  otherFieldValues?: string[];
}

/**
 * react-select passes these to every custom component. Typed structurally on
 * purpose: kula's `Select` runs react-select **5**, while core-web's own
 * dependency is **4** — importing `MenuProps` here would type against v4 and
 * compile clean while misbehaving at runtime.
 */
interface RecipientMenuProps {
  innerProps?: Record<string, unknown>;
  /**
   * Which side of the field react-select placed the panel on. Its own menu CSS
   * reads this; ours is Chakra, so the flip has to be honoured here or `auto`
   * moves the panel's anchor without moving the panel.
   */
  placement?: 'bottom' | 'top' | 'auto';

  /** react-select's processed options — kula-wrapped, so they carry `__original`. */
  options?: ReadonlyArray<{ value?: unknown }>;
  getValue: () => ReadonlyArray<{ value?: unknown }>;
  setValue: (_value: unknown, _action: string) => void;
  selectProps: {
    inputValue?: string;
    onMenuClose?: () => void;
    recipientPicker?: RecipientPickerConfig;
  };
}

/**
 * The roles a user holds on this job, as the row shows them.
 *
 * One role reads plainly. More than one names the first and counts the rest,
 * with every role in the hover — the row has a name and an address to carry
 * too, and three spelled-out roles would push them out of it.
 *
 * The hover lists bare names. Only the badge carries "on job", because only the
 * badge stands alone; repeating it down four lines is the same three words four
 * times over, against the names the reader is there to compare.
 */
const HOVER_DELAY_MS = 150;

/**
 * The hover itself, with `open` owned here rather than by Radix.
 *
 * Radix opens on `pointermove`, behind a latch it raises on the first move and
 * lowers only on `pointerleave`. Closing does not lower it — and there are two
 * ways to close: the trigger's own `pointerdown`/`click`, and the content's
 * dismissable layer, which counts a press on the trigger as a press outside
 * itself. Every row in this menu is a checkbox and the badge sits inside one,
 * so ticking a person left their badge with a dead hover until the pointer
 * travelled off it and back. QA read that as a "& +1" with nothing behind it.
 *
 * Owning `open` sidesteps the latch entirely: whatever Radix closed it for, the
 * next movement over the badge opens it again, and leaving always closes it.
 * Cancelling the individual close paths instead leaves the tooltip stuck open,
 * because dismissal is what Radix also uses to put it away.
 */
const RolesTooltip = ({
  all,
  children,
}: {
  all: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const openTimer = useRef<number | undefined>(undefined);

  const hide = () => {
    window.clearTimeout(openTimer.current);
    openTimer.current = undefined;
    setOpen(false);
  };

  const show = () => {
    // `pointermove` fires continuously; without this every pixel would restart
    // the wait and the tooltip would never arrive while the pointer drifts.
    if (open || openTimer.current) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = undefined;
      setOpen(true);
    }, HOVER_DELAY_MS);
  };

  useEffect(() => () => window.clearTimeout(openTimer.current), []);

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={(next) => (next ? show() : hide())}>
        {/* Chakra's Box rather than the child itself: `asChild` hands the
            trigger its ref, and only a forwardRef element can take it.
            `aria-label` carries the same list to anyone who never hovers. */}
        <TooltipTrigger asChild>
          <Box
            as="span"
            display="inline-flex"
            minW={0}
            aria-label={all}
            onPointerMove={show}
            onPointerLeave={hide}
          >
            {children}
          </Box>
        </TooltipTrigger>
        <TooltipContent side="top" collisionPadding={12}>
          {all}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const RolesHover = ({
  tags,
  children,
}: {
  tags?: string[];
  children: ReactNode;
}) => {
  // Nothing is hidden behind a single role, so nothing to open.
  if ((tags ?? []).length < 2) return children;

  return <RolesTooltip all={(tags ?? []).join(', ')}>{children}</RolesTooltip>;
};

const OnJobTags = ({ tags }: { tags?: string[] }) => {
  const summary = onJobTagSummary(tags);
  if (!summary) return null;

  return (
    <RolesHover tags={tags}>
      <Badge color="grey" data-test-id="recipient-option-tag">
        {summary}
      </Badge>
    </RolesHover>
  );
};

/**
 * Role rows are 44px with an 18px initials chip; user rows are 52px with a 24px
 * avatar and a second line. Both share the 12px gutter and 6px column gaps.
 */
const SectionRow = ({
  option,
  checked,
  onToggle,
}: {
  option: RecipientOption;
  checked: boolean;
  onToggle: () => void;
}) => {
  const isRole = Boolean(option.initials);
  const isDisabled = Boolean(option.disabled);
  // A person's row has a line under the name; a role token does not.
  const hasSecondLine = Boolean(option.roleLine || option.email);

  return (
    <Flex
      as="button"
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={isDisabled || undefined}
      // Deliberately NOT the `disabled` attribute. A disabled <button> has its
      // mouse events suppressed by the browser, so the press never dispatches
      // and never reaches the menu's `onMouseDown` — focus still leaves
      // react-select's input and it closes the menu on blur. `aria-disabled`
      // plus no `onClick` conveys and enforces the same thing, while letting
      // the press through so focus can be preserved.
      tabIndex={isDisabled ? -1 : undefined}
      // The mark and avatar belong to the name, not to the pair of lines —
      // centred, they float between the two. One line has nothing to hang from.
      align={hasSecondLine ? 'flex-start' : 'center'}
      gap="6px"
      px="12px"
      pt={hasSecondLine ? TWO_LINE_ROW_PADDING_TOP : undefined}
      h={isRole ? '44px' : '52px'}
      w="100%"
      textAlign="left"
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.5 : 1}
      borderBottom="1px solid"
      borderColor="secondary-100"
      _hover={isDisabled ? undefined : { bg: 'secondary-25' }}
      onMouseDown={(event) => event.preventDefault()}
      onClick={isDisabled ? undefined : onToggle}
      data-test-id={`recipient-option-${option.value}`}
    >
      {/* The row owns the click; the box is the indicator, so a tick and a
          row click cannot both fire and cancel each other out. */}
      <Box pointerEvents="none" display="flex">
        <Checkbox size="sm" checked={checked} readOnly />
      </Box>

      {isRole ? (
        <Flex
          align="center"
          justify="center"
          minW="18px"
          h="18px"
          px="4px"
          flexShrink={0}
          borderRadius="4px"
          // `pastel-3-light`, not the `pastel-3light` spelling the raw
          // design-tokens package uses — Chakra cannot resolve that one and
          // silently emits no background at all.
          bg="pastel-3-light"
          data-test-id="recipient-option-initials"
        >
          {/* kula Text bottoms out at 12px (span/small/caption) and these
              initials are 10px, so this one label stays local. */}
          <Box as="span" fontSize="10px" fontWeight={650} color="base-black">
            {option.initials}
          </Box>
        </Flex>
      ) : (
        <Avatar size={24} src={option.image} name={option.label} />
      )}

      <Box flex="1" minW={0}>
        <Flex align="center" gap="6px" minW={0}>
          {/* The `minW={0}` box, not the Text: `truncate` needs somewhere to
              shrink to, and a flex item's floor is its own content — without it
              a long role name sizes the row and pushes the count off the end. */}
          <Box minW={0}>
            <Text variant={isRole ? 'p' : 'h4'} truncate>
              {option.label}
            </Text>
          </Box>
          {/* How many people the role reaches. Roles only — a person is one
              person, and the row already says who. The same grey tag the role
              names and the disabled reasons wear, so one row speaks with one
              voice; `shrink-0` because kula's Badge is not, and a long role
              name would otherwise eat the count. */}
          {option.memberCount !== undefined && (
            <Badge
              color="grey"
              className="shrink-0"
              data-test-id="recipient-option-count"
            >
              {memberCountLabel(option.memberCount)}
            </Badge>
          )}
        </Flex>
        {/* The roles that put this person on the job, where a plain user row
            shows an address — the hiring team sends none, and the roles are the
            reason they are in the list at all. All of them, named: the line has
            the row to itself, so there is room. */}
        {option.roleLine ? (
          // kula's `Text` opens a tooltip of its own for a truncating label,
          // and only once the label actually overflows — which is exactly the
          // contract this line wants. Every role is named, so a hover that
          // fires regardless would repeat what is already on screen.
          <Text variant="p" truncate data-test-id="recipient-option-roles">
            {option.roleLine}
          </Text>
        ) : (
          option.email && (
            <Text variant="p" truncate>
              {option.email}
            </Text>
          )
        )}
      </Box>

      {option.hint ? (
        // The same tag the roles wear, so the one slot on the right of a row
        // reads as one thing whatever it happens to be saying. `shrink-0`
        // because kula's Badge is not, and a long name would otherwise squeeze
        // the reason for the row being greyed out down to nothing.
        <Badge
          color="grey"
          className="shrink-0"
          data-test-id="recipient-option-hint"
        >
          {option.hint}
        </Badge>
      ) : (
        // Not on a row that already counts its roles underneath — the badge
        // would be the same fact twice over, on the same row.
        !option.roleLine && <OnJobTags tags={option.tags} />
      )}
    </Flex>
  );
};

/** Also how `closeMenuOnScroll` tells the menu's own scrolling from the page's. */
export const MENU_TEST_ID = 'recipient-picker-menu';

/** Where centring already started a two-line row's content: (52px − ~38px) / 2. */
const TWO_LINE_ROW_PADDING_TOP = '7px';

const SEARCHING_LABEL = 'Searching…';
const LOADING_LABEL = 'Loading…';

/**
 * A section whose rows do not answer the question being asked, in place of those
 * rows rather than above them.
 *
 * It used to sit on top and leave the previous answer showing and pickable. That
 * reads fine and picks badly: the names under it are the answer to a search the
 * sender has already typed past, and a picked recipient is committed on Add
 * without ever being looked at again. Taking the rows away is the only thing
 * that makes "you cannot choose from this yet" true rather than merely said.
 *
 * Three rows tall, so the section neither collapses to a sliver nor jumps when
 * the real rows replace it.
 */
const LoadingSection = ({ searching }: { searching: boolean }) => (
  <Flex
    align="center"
    justify="center"
    gap="8px"
    minH="132px"
    borderBottom="1px solid"
    borderColor="secondary-100"
    // The rows are gone, not merely covered — say so, so a reader on a screen
    // reader is not left waiting on a list that is not coming back yet.
    aria-busy="true"
    // kula's spinner announces itself (`role="status"`); this does not repeat it.
    data-test-id="recipient-section-loading"
  >
    <SpinnerLoader size="sm" />
    <Text variant="p" color="muted">
      {searching ? SEARCHING_LABEL : LOADING_LABEL}
    </Text>
  </Flex>
);

/**
 * A later page on its way, under the rows it is going to join.
 *
 * Paging keeps its rows — they are still the right answer, and blanking the list
 * under someone who scrolled to the end of it would be the worse lie.
 */
const LoadingMoreRow = () => (
  <Flex
    align="center"
    gap="6px"
    px="12px"
    h="44px"
    borderBottom="1px solid"
    borderColor="secondary-100"
    data-test-id="recipient-section-loading-more"
  >
    <SpinnerLoader size="sm" />
    <Text variant="p" color="muted">
      {LOADING_LABEL}
    </Text>
  </Flex>
);

/**
 * The To/Cc/Bcc dropdown: job roles, Kula roles and users in three accordions.
 *
 * Rendered as react-select's `Menu` through `reactSelectProps`, so kula's own
 * chips, placeholder, error display and card chrome all stay — only the panel
 * is ours. Selection is staged locally and committed on Add, which react-select
 * has no notion of; `setValue` is what makes that possible.
 *
 * Search is react-select's own input (`selectProps.inputValue`): the two role
 * sections filter in memory, and the caller re-queries users on the same term.
 */
export const RecipientPickerMenu = (props: RecipientMenuProps): ReactNode => {
  const { selectProps, getValue, setValue, innerProps, options, placement } =
    props;
  const { recipientPicker, onMenuClose, inputValue } = selectProps;
  const sections = recipientPicker?.sections ?? [];
  const searching = Boolean(inputValue?.trim());
  const onEndReached = recipientPicker?.onEndReached;

  /**
   * What the field holds right now.
   *
   * Deliberately not memoised on `getValue`: react-select can hand back the
   * same function identity across value changes, so a memo keyed on it freezes
   * this at whatever the field held on the menu's first render — which, if the
   * value had not hydrated yet, is nothing at all. It maps a handful of
   * options; recomputing every render is cheaper than being wrong.
   */
  const committed = (getValue() ?? []).map((option) =>
    String(option?.value ?? '')
  );

  /**
   * What the user has touched, as a pair of deltas rather than a copy of the
   * selection.
   *
   * The panel used to copy the field into state, and Add writes the ticks back
   * over the field — so a panel that opened before the recipients had landed
   * showed nothing ticked and dropped them all the moment it was pressed
   * (CORE-5384). Holding a whole-selection snapshot taken at the first tick
   * only narrowed that window: the snapshot could still be taken over an empty
   * field, and anything arriving afterwards was overwritten just the same.
   *
   * Deltas close it. Every render reads the field as it is now, minus what the
   * user removed, plus what they added — so a recipient that lands late is
   * carried whether it arrives before the first tick or after, while the two
   * things the user actually did are never moved under them. Cancel is still
   * "never commit": the menu unmounts, and there is no reset to write.
   */
  const [added, setAdded] = useState<string[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);

  const staged = [
    ...committed.filter((value) => !removed.includes(value)),
    // Only what the field does not already hold, so a value the user ticked
    // and that then arrived on its own is not listed twice.
    ...added.filter((value) => !committed.includes(value)),
  ];

  /**
   * Unmemoised, like `committed` and `staged` above and for the same reason: the
   * field's value changes under us, and a stale budget would grey out rows that
   * are free or offer rows that are not.
   *
   * Tallied together with the sibling field, so a person on both Cc and Bcc is
   * one — which is how the backend counts them too.
   */
  const costModel = recipientPicker?.costModel ?? EMPTY_COST_MODEL;
  const tally = tallyRecipients(
    [...staged, ...(recipientPicker?.otherFieldValues ?? [])],
    costModel
  );
  const remaining =
    recipientPicker?.limit === undefined
      ? Infinity
      : recipientPicker.limit - tally.total;

  const toggle = (value: string) => {
    const ticked = staged.includes(value);
    setAdded((current) =>
      ticked
        ? current.filter((item) => item !== value)
        : current.includes(value)
          ? current
          : [...current, value]
    );
    setRemoved((current) =>
      ticked
        ? current.includes(value)
          ? current
          : [...current, value]
        : current.filter((item) => item !== value)
    );
  };

  // Nothing to apply — the same values are already on the field, which on a row
  // with none ticked and none committed means Add would do nothing at all. Still
  // enabled once they differ, so unticking everything and pressing Add clears the
  // row rather than leaving removal to the chips.
  const unchanged =
    staged.length === committed.length &&
    staged.every((value) => committed.includes(value));

  // Row heights are fixed (44/52px), so a threshold in pixels is a threshold in
  // rows: ask for the next page about two rows from the end, not at the very
  // bottom, so the list grows before the scroll stops.
  const handleScroll = (event: { currentTarget: HTMLDivElement }) => {
    if (!onEndReached) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 96) onEndReached();
  };

  const commit = () => {
    const byValue = recipientPicker?.optionByValue;
    // kula's `useSelectValue` maps every committed option to `__original` and
    // then reads `.value` off it — a plain `{value,label}` makes that throw, so
    // onChange never fires and Add silently does nothing. Prefer react-select's
    // own processed option (already wrapped); otherwise wrap it the same way.
    const wrapped = new Map(
      (options ?? []).map((option) => [String(option?.value ?? ''), option])
    );
    setValue(
      staged.map((value) => {
        const known = wrapped.get(value);
        if (known) return known;
        const original = byValue?.get(value) ?? { value, label: value };
        return { ...original, __original: original };
      }),
      'select-option'
    );
    onMenuClose?.();
  };

  /**
   * Fit the panel to the room the window actually has.
   *
   * react-select works this out too, but hands the answer to `MenuList` — and
   * this panel renders its own list, so the number never arrives. Measuring here
   * is the same sum against the same viewport: `menuPosition: 'fixed'` means the
   * panel's own rect is already in viewport coordinates.
   *
   * Read off the edge the panel hangs from, never off its height: its top when
   * it sits below the field, its bottom when it has been flipped above. Either
   * way the anchor is the field, so shrinking the list cannot move what the next
   * measurement reads — which a height-derived one would, once round the loop.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState<number | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;
      setRoom(
        placement === 'top'
          ? rect.bottom - VIEWPORT_GUTTER
          : window.innerHeight - rect.top - VIEWPORT_GUTTER
      );
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [placement]);

  // The footer is not negotiable — a panel whose Add button is past the fold
  // cannot be committed at all — so the list gives up the difference, down to a
  // floor of about three rows.
  const listMaxHeight =
    room === null
      ? LIST_MAX_HEIGHT
      : Math.min(
          LIST_MAX_HEIGHT,
          Math.max(LIST_MIN_HEIGHT, room - FOOTER_HEIGHT)
        );

  const ordered = SECTION_ORDER.map((id) =>
    sections.find((section) => section.id === id)
  ).filter((section): section is RecipientSection => Boolean(section));

  return (
    <Box
      ref={panelRef}
      {...innerProps}
      // Keeps focus in react-select's input for every press inside the menu.
      // Without it a press that lands on menu chrome rather than an enabled row
      // — a disabled option (a disabled <button> fires no mouse events, so the
      // press hits this container), a section header, plain padding — blurs the
      // input, and react-select closes the menu on blur. `onClick` still fires,
      // so rows and the Cancel/Add buttons are unaffected.
      onMouseDown={(event) => event.preventDefault()}
      // react-select positions the portal at the field's top edge when it flips
      // the panel above, and leaves lifting it clear of the field to the menu's
      // own CSS — which is emotion's, and this panel is Chakra. Without this the
      // flip only moves the anchor: the panel still hangs downwards, over the
      // field it belongs to.
      transform={placement === 'top' ? 'translateY(-100%)' : undefined}
      bg="white"
      borderRadius="6px"
      border="1px solid"
      borderColor="secondary-100"
      boxShadow="drop-shadows-bottom"
      overflow="hidden"
      data-test-id={MENU_TEST_ID}
    >
      <Box maxH={`${listMaxHeight}px`} overflowY="auto" onScroll={handleScroll}>
        <Accordion.Root
          type="multiple"
          defaultValue={SECTION_ORDER}
          indicatorPosition="left"
          // A custom indicator opts out of kula's built-in chevron rotation, so
          // the open-state flip is re-declared here against our own class.
          indicator={
            <Flex
              align="center"
              justify="center"
              w="52px"
              // Stretch rather than a fixed 52px height: the trigger's own
              // height already includes its bottom rule, so a square cell would
              // stand 1px proud of the content box and paint over it — which is
              // exactly how that rule came to be missing under the chevron.
              alignSelf="stretch"
              flexShrink={0}
              borderRight="1px solid"
              borderColor="secondary-100"
            >
              <ChevronDown
                className="recipient-chevron transition-transform duration-200"
                size={18}
                color={colors['secondary-700']}
              />
            </Flex>
          }
          // The header closes itself off with a rule across the full width,
          // chevron cell included — without it the only thing parting a section
          // heading from its first row is the change from grey to white.
          triggerClassName="h-[52px] gap-0 p-0 border-b border-secondary-100 bg-secondary-50 hover:bg-secondary-50 [&[data-state=open]_.recipient-chevron]:rotate-180"
          // Rows and headers draw their own separators, so the item wrapper's
          // would double up at every section boundary.
          itemClassName="border-b-0"
          contentClassName="[&>div]:pb-0"
          // Radix would otherwise steal the keystrokes from react-select's input.
          onKeyDown={(event) => event.stopPropagation()}
        >
          {ordered.map((section) => {
            const { Icon } = SECTION_CHROME[section.id];

            return (
              <Accordion.Item key={section.id} value={section.id}>
                <Accordion.Trigger
                  data-test-id={`recipient-section-${section.id}`}
                >
                  <Flex
                    align="center"
                    gap="6px"
                    pl="12px"
                    pr="12px"
                    w="100%"
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                    h="52px"
                  >
                    <Icon size={18} color={colors['secondary-700']} />
                    <Flex align="center" gap="8px">
                      <Text variant="overhead" color="heading">
                        {section.title}
                      </Text>
                      {section.count !== undefined && (
                        <Badge type="outline" color="white">
                          {section.count}
                        </Badge>
                      )}
                    </Flex>
                  </Flex>
                </Accordion.Trigger>
                <Accordion.Content>
                  {/* In place of the rows, not over them: while this is up there
                      is nothing here to pick, which is the point of it. */}
                  {section.isReloading ? (
                    <LoadingSection searching={searching} />
                  ) : section.options.length === 0 ? (
                    <Text variant="p" style={{ padding: '12px' }}>
                      No matches
                    </Text>
                  ) : (
                    section.options.map((option) => {
                      // From the ORIGINAL option, before any override below. The
                      // expression is `!option.disabled && staged.includes(…)`,
                      // so handing it a row we have just disabled would render an
                      // already-picked recipient unticked — and Add would drop
                      // them without a word.
                      const checked =
                        !option.disabled && staged.includes(option.value);
                      const adds =
                        checked || option.disabled
                          ? 0
                          : costOfAdding(option.value, tally, costModel);
                      // Per row, not one flag for the menu: "there is no room
                      // left" and "this 300-person role will not fit in the 40
                      // you have" are different rows, and the cost tells them
                      // apart for free.
                      //
                      // `adds > 0` first, because a selection can start over the
                      // cap — a prefilled activity whose roles have grown — and
                      // `remaining` is then negative. Without it every row costing
                      // nothing, including the ticked ones, would be greyed out,
                      // and the sender could neither send nor cut it back.
                      const blocked = adds > 0 && adds > remaining;

                      return (
                        <SectionRow
                          key={option.value}
                          option={
                            blocked
                              ? {
                                  ...option,
                                  disabled: true,
                                  hint:
                                    remaining <= 0
                                      ? recipientLimitHint(
                                          recipientPicker?.limit
                                        )
                                      : RECIPIENT_OVER_LIMIT_HINT,
                                }
                              : option
                          }
                          checked={checked}
                          onToggle={() => toggle(option.value)}
                        />
                      );
                    })
                  )}
                  {/* Under the rows, not over them: a later page is on its way,
                      or is there to be asked for, and either way the ones already
                      listed are not going anywhere. Also the sentinel the scroll
                      handler watches for, so it has to be in the flow. */}
                  {!section.isReloading &&
                    (section.isLoading || section.hasMore) && (
                      <LoadingMoreRow />
                    )}
                </Accordion.Content>
              </Accordion.Item>
            );
          })}
        </Accordion.Root>
      </Box>

      <Flex
        justify="flex-end"
        gap="6px"
        p="12px"
        borderTop="1px solid"
        borderColor="secondary-100"
      >
        <Button
          variant="secondary"
          size="regular"
          onClick={() => onMenuClose?.()}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="regular"
          onClick={commit}
          disabled={unchanged}
          data-test-id="recipient-picker-add"
        >
          {staged.length > 0 ? `Add (${staged.length})` : 'Add'}
        </Button>
      </Flex>
    </Box>
  );
};
